import { useMemo } from 'react'

// Stripe processing fee: 2.9% + $0.30, floored to cents (matches Scale).
export function calculateStripeProcessingFee(amount) {
    const a = Number(amount) || 0
    const fee = a * 0.029 + 0.3
    return Math.floor(fee * 100) / 100
}

// Compute admin fee + per-party allocation + processing fee for the
// currently-selected client, mirroring Scale's useAdminFee.
//
// Inputs:
//   subtotal:        sum of line item amounts
//   selectedOption:  'one' | 'all' (or 'both' legacy)
//   selectedParty:   client_id when selectedOption === 'one'
//   invoice_allocation_party: '50_percent' | 'equally_split' | null
//   clients:         flat array of all parties involved (when selectedOption='all')
//   selectedClientId: id of the party currently being previewed
//   mediatorSettings: from mediator.mediator_settings + mediator.admin_fee
//   isGscCase, isAdminFeeCollected, isCouponApplied
//
// Returns the same shape Scale's useAdminFee exposes.
export default function useAdminFee({
    subtotal = 0,
    selectedOption = 'all',
    selectedParty = null,
    invoiceAllocationParty = null,
    clients = [],
    selectedClientId = null,
    mediatorRecord = null,
    isGscCase = false,
    isAdminFeeCollected = false,
    isCouponApplied = false,
    mediatorProcessingFeeToClient = false,
    // TRUE when this hook is driving the FIXED-invoice editor rather than the
    // hourly create/edit flow.
    //
    // The `/ 2` in the percentage branches below is an HOURLY assumption: there,
    // the ledger a percentage is taken from is already the combined case total,
    // so half of it is one party's share. A fixed invoice's `subtotal` is the
    // amount for ONE invoice, so halving it charges half the fee it should. The
    // mediator portal draws exactly this distinction —
    // FixedUpdateInvoice/common/useAdminFee.js: `isFixedPriceInvoice ? subtotal
    // : subtotal / 2` — and forces the flag true throughout its fixed flow.
    //
    // It also suppresses the per-party division: a flat admin fee is charged per
    // party, not shared between them, and on a fixed "both" edit each invoice
    // carries the whole fee.
    isFixedPriceInvoice = false,
}) {
    return useMemo(() => {
        // Use the SHAPED admin-fee object (safeMediator.adminFee = {admin_fee_type,
        // admin_fee_amount, admin_fee_pay_by, custom_percentage_for_coupon}). NOT
        // mediatorRecord.admin_fee — that's the who-pays text string ('client'/
        // 'mediator'), which is truthy and would short-circuit this lookup, leaving
        // every config read undefined (fee→0, payBy→'mediator', breaking
        // is_admin_fee_added_in_line_item for client-pays mediators).
        const adminFeeCfg = mediatorRecord?.adminFee || {}
        const mediatorSettings = mediatorRecord?.mediatorSettings || mediatorRecord?.mediator_settings || {}
        const adminFeeType = adminFeeCfg.admin_fee_type || 'flat'
        const adminFeeAmount = Number(adminFeeCfg.admin_fee_amount) || 0
        const customCouponPct = Number(adminFeeCfg.custom_percentage_for_coupon) || 0
        const adminFeePayBy = adminFeeCfg.admin_fee_pay_by || 'mediator'

        // Total admin fee (across the whole invoice — split applied below).
        let totalAdminFee = 0
        if (isAdminFeeCollected) {
            totalAdminFee = 0
        } else if (isGscCase && mediatorSettings?.gsc_admin_fee != null) {
            totalAdminFee = Number(mediatorSettings.gsc_admin_fee) || 0
        } else if (isCouponApplied) {
            // See `isFixedPriceInvoice` above: the /2 is an hourly-ledger
            // assumption and must not apply to a fixed invoice's subtotal.
            const pctBase = isFixedPriceInvoice ? subtotal : subtotal / 2
            totalAdminFee = (pctBase / 100) * customCouponPct
        } else if (adminFeeType === 'percentage') {
            const pctBase = isFixedPriceInvoice ? subtotal : subtotal / 2
            totalAdminFee = (pctBase / 100) * adminFeeAmount
        } else {
            totalAdminFee = adminFeeAmount
        }

        // Per-party allocation
        const splitFactor = (() => {
            // A fixed invoice is billed per party at its own full total, so it
            // is never divided here.
            if (isFixedPriceInvoice) return 1
            if (selectedOption === 'one') return 1
            if (invoiceAllocationParty === '50_percent') return 2
            const len = (clients || []).filter(Boolean).length
            return len > 0 ? len : 2
        })()
        const perPartyShare = subtotal / splitFactor

        // PROCESSING FEE IS NOT BILLED ON THE INVOICE.
        //
        // The card fee is collected by the payment gateway at the moment the
        // party pays, and only if they pay by card — an ACH payer owes nothing
        // extra. Putting it on the invoice charges it to everyone in advance,
        // at a rate that cannot be known until the payment method is chosen.
        //
        // The mediator portal disables it outright for the same reason
        // (fixedUpdateInvoiceUiFlags.js: DISABLE_FIXED_UPDATE_PROCESSING_FEE =
        // true, applied through Step 2/3 and the payload). This mirrors that.
        // `mediatorProcessingFeeToClient` still decides who ABSORBS the fee at
        // the gateway; it just no longer adds a line here.
        //
        // Flip to restore the old behaviour, as the portal's flag does.
        const DISABLE_INVOICE_PROCESSING_FEE = true
        const processingFee =
            !DISABLE_INVOICE_PROCESSING_FEE && mediatorProcessingFeeToClient
                ? calculateStripeProcessingFee(perPartyShare)
                : 0

        // is_admin_fee_added_in_line_item: true when client absorbs admin fee
        // and no coupon. Otherwise the fee is silently deducted from mediator
        // payout via Stripe `application_fee_amount`.
        const is_admin_fee_added_in_line_item = adminFeePayBy === 'client' && !isCouponApplied

        // ADMIN FEE PER PARTY.
        //
        // A FLAT fee is charged per party, not shared between them: the
        // mediator's $250 lands on every invoice on the case (verified in
        // hourly_invoice_payment — admin_fee = 250 on each row, and the
        // mediator portal renders "+ $250 admin fee" against each party).
        // Dividing it by the party count showed $125 on a 2-party case where
        // the portal showed $250, and would have shown $83.33 once the party
        // list was fixed and the count became 3.
        //
        // A PERCENTAGE fee is different: it is derived from the case subtotal
        // above, so it IS a case-wide total and each party owes a share of it.
        const adminFeePerParty =
            adminFeeType === 'percentage' || isCouponApplied
                ? (splitFactor > 0 ? totalAdminFee / splitFactor : totalAdminFee)
                : totalAdminFee

        const displayProcessingFee = processingFee
        const displayTotal =
            perPartyShare +
            (is_admin_fee_added_in_line_item ? adminFeePerParty : 0) +
            processingFee

        // Locate the currently-previewed client's allocation slice.
        const selected = (clients || []).find((c) => c?.client_id === selectedClientId) || null
        const invoiceAllocationData = selected
            ? {
                  percentage: Math.round((100 / splitFactor) * 10) / 10,
                  amount: perPartyShare,
              }
            : { percentage: 100, amount: subtotal }

        return {
            adminFeeAmount: totalAdminFee.toFixed(2),
            adminFeeAmountNumeric: totalAdminFee,
            adminFeePerParty,
            isAdminFeeCollected,
            is_admin_fee_added_in_line_item,
            displayTotal,
            displayProcessingFee,
            invoiceAllocationData,
            splitFactor,
            perPartyShare,
        }
    }, [
        subtotal,
        selectedOption,
        selectedParty,
        invoiceAllocationParty,
        clients,
        selectedClientId,
        mediatorRecord,
        isGscCase,
        isAdminFeeCollected,
        isCouponApplied,
        mediatorProcessingFeeToClient,
        isFixedPriceInvoice,
    ])
}

// Compute reminder dates from a due date + mediator settings. Mirrors
// Scale's `reminderList` util — falls back to fixed offsets if settings
// are missing. YYYY-MM-DD strings are parsed as local midnight to keep
// reminder dates in the user's timezone (UTC parse would shift dates by
// one day for users west of UTC).
export function computeReminderList(dueDate, settings) {
    if (!dueDate) return null
    let d
    if (typeof dueDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dueDate)) {
        const [y, m, day] = dueDate.slice(0, 10).split('-')
        d = new Date(Number(y), Number(m) - 1, Number(day))
    } else {
        d = new Date(dueDate)
    }
    if (Number.isNaN(d.getTime())) return null
    const cfg = settings || {}
    const offsets = {
        first: Number(cfg.due_date_email_days) || 0, // email on due date
        second: -Number(cfg.first_reminder_days || 3),
        third: -Number(cfg.second_reminder_days || 7),
        fourth: -Number(cfg.third_reminder_days || 14),
    }
    const fmt = (offset) => {
        const x = new Date(d)
        x.setDate(x.getDate() + offset)
        return x.toISOString().split('T')[0]
    }
    return {
        firstReminder: fmt(offsets.first),
        secondReminder: fmt(offsets.second),
        thirdReminder: fmt(offsets.third),
        fourthReminder: fmt(offsets.fourth),
    }
}
